import type { ApacCandidateHeader } from '@seek/apac-candidate-header-footer';
import { AUTHENTICATION_TYPES } from '@seek/apac-candidate-header-footer/types';
import type { ComponentProps } from 'react';

import useAuthenticated from 'src/hooks/useAuthenticated';
import { useCandidateDetails } from 'src/modules/hooks/useCandidateDetailsQuery';
import { useSetLanguage } from 'src/store/appConfig/hooks';
import { useSelector } from 'src/store/react';
import {
  selectIsHomePage,
  selectIsJDP,
  selectLocationQuery,
} from 'src/store/selectors';

import { useLanguageSwitcher } from './useLanguageSwitcher';
import { useReturnUrl } from './useReturnUrl';

const useAuthenticationStatus = (): ComponentProps<
  typeof ApacCandidateHeader
>['authenticationStatus'] => {
  const authenticateState = useAuthenticated();

  switch (authenticateState) {
    case 'authenticated':
      return AUTHENTICATION_TYPES.AUTHENTICATED;
    case 'unauthenticated':
      return AUTHENTICATION_TYPES.UNAUTHENTICATED;
    case 'pending':
      return AUTHENTICATION_TYPES.AUTH_PENDING;
  }
};

export const useHeaderProps = () => {
  const authenticationStatus = useAuthenticationStatus();

  const { email, firstName } = useCandidateDetails();
  const isJDP = useSelector(selectIsJDP);
  const isHomePage = useSelector(selectIsHomePage);
  const isLoggedInHomePage =
    isHomePage && authenticationStatus === 'authenticated';
  const setLanguage = useSetLanguage();
  const query = useSelector(selectLocationQuery);

  // Remove ref query param for logout link on the logged in home page
  if (isLoggedInHomePage && query?.ref === 'recs-feed') {
    query.ref = undefined;
    query.redirected = undefined;
  }

  const returnUrlForPostAuth = useReturnUrl({ query });

  const userProfile = {
    firstName,
    lastName: '',
    email,
  };
  const onLanguageSwitch: ComponentProps<
    typeof ApacCandidateHeader
  >['onLanguageSwitch'] = (lang) => setLanguage(lang);

  const languageSwitcher = useLanguageSwitcher();

  return {
    authenticationStatus,
    isEnableDivider: isJDP,
    languageSwitcher,
    onLanguageSwitch,
    returnUrlForPostAuth: returnUrlForPostAuth || '',
    userProfile,
  };
};

export const useFooterProps = () => {
  const authenticationStatus = useAuthenticationStatus();

  return { authenticationStatus, widthMode: 'large' as const };
};
